import { useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useLocation } from "react-router-dom";

import CustomBreadcrumb from "../../../components/customBreadcrumb/CustomBreadcrumb";
import EmptyMessage from "../../../components/emptyMessage/EmptyMessage";
import MainLoader from "../../../components/loading/MainLoader";
import SearchBar from "../../../components/searchBar/SearchBar";
import StatusAlertModel from "../../../components/statusAlertModel/StatusAlertModel";
import Title from "../../../components/title/Title";
import { IProfileData } from "../../../interfaces/profileData";
import { IProtocolReportSubscriptionOptions } from "../../../interfaces/protocolReportSubscriptionOptions";
import { ISuccessState } from "../../../interfaces/successState";
import {
  getProfileData,
  getProtocolReportSubscriptionOptions,
} from "../../../services/dashboard";
import ConfirmSubscribe from "./confirmSubscribe/ConfirmSubscribe";
import SubscribeOptions from "./subscribeOptions/SubscribeOptions";
import Unsubscribe from "./unsubscribe/Unsubscribe";

const DetailedReport = () => {
  const location = useLocation();

  const [selectedIndex, setSelectedIndex] = useState<"1" | "2">("1");
  const [showConfirmation, setShowConfirmation] = useState<boolean>(false);
  const [showUnsubscribe, setShowUnsubscribe] = useState<boolean>(false);
  const [hasPrivilage, setHasPrivilege] = useState<boolean>(false);
  const [subscriptionOptions, setSubscriptionOptions] =
    useState<IProtocolReportSubscriptionOptions>();
  const [loading, setLoading] = useState<boolean>(false);
  const [serviceId, setServiceId] = useState<string>("");
  const [isSuccess, setIsSuccess] = useState<ISuccessState | undefined>();

  useEffect(() => {
    const selectedCircuitId = (location.state as any)?.selectedCircuitId;
    if (selectedCircuitId) {
      searchHandler(selectedCircuitId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  const searchHandler = (value: string) => {
    setServiceId(value);
    setHasPrivilege(false);
    setShowConfirmation(false);
    setShowUnsubscribe(false);

    if (!value) {
      return;
    }

    setLoading(true);
    getProfileData(value)
      .then((profileData: IProfileData) => {
        setShowUnsubscribe(profileData.priviledges.protocol_Report === "true");

        const hasPrivilage: boolean =
          profileData.priviledges.purchase_Protocol_Report === "true";
        setHasPrivilege(true);

        if (hasPrivilage) {
          getSubscriberOptions(value);
          return;
        }

        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
        setIsSuccess({ isSuccess: false, message: err.message });
      });
  };

  const getSubscriberOptions = (serviceId: string) => {
    getProtocolReportSubscriptionOptions(serviceId)
      .then((data: IProtocolReportSubscriptionOptions) => {
        setSubscriptionOptions(data);
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
        setIsSuccess({ isSuccess: false, message: err.message });
      });
  };

  const renderUI = (): JSX.Element => {
    if (!serviceId) {
      return <EmptyMessage show message={"Please enter service ID"} />;
    }

    if (!hasPrivilage) {
      return (
        <EmptyMessage
          show
          message={"You don't have privilege to subscribe protocol report"}
        />
      );
    }

    if (showUnsubscribe) {
      return <Unsubscribe setLoading={setLoading} serviceId={serviceId} />;
    }

    if (!subscriptionOptions) {
      return (
        <EmptyMessage show message={"Couldn't get subscription options"} />
      );
    }

    if (showConfirmation) {
      return (
        <ConfirmSubscribe
          setShowConfirmation={setShowConfirmation}
          setLoading={setLoading}
          selectedPackage={subscriptionOptions.packages.find(
            (pkg) => pkg.packageid === selectedIndex
          )}
          serviceId={serviceId}
        />
      );
    }

    return (
      <SubscribeOptions
        selectedIndex={selectedIndex}
        setShowConfirmation={setShowConfirmation}
        setSelectedIndex={setSelectedIndex}
        subscriptionOptions={subscriptionOptions}
        selectedPackage={subscriptionOptions.packages.find(
          (pkg) => pkg.packageid === selectedIndex
        )}
      />
    );
  };

  return (
    <div className="page-container">
      <MainLoader show={loading} />
      <CustomBreadcrumb
        items={[
          { path: "/", title: "Home" },
          { path: "/serviceLineUp", title: "Service Line-up" },
          { path: "/serviceLineUp/usageSummery", title: "Usage Summary" },
          { path: "", title: "Enable/Disable Protocol Report" },
        ]}
      />

      <Title>Protocol Report</Title>

      <Row className="justify-content-center mt-2">
        <Col className="col-md-6">
          <SearchBar
            placeholder="Broadband ID"
            onSearch={searchHandler}
            defaultValue={serviceId}
          />
        </Col>
      </Row>

      <Row className="page-sub-container mt-3">
        <Col>{renderUI()}</Col>
      </Row>
      <StatusAlertModel isSuccess={isSuccess} setIsSuccess={setIsSuccess} />
    </div>
  );
};

export default DetailedReport;
